import React, { useCallback, useContext, useState } from 'react';
import styled from 'styled-components';

import { InsightsContentContext } from 'contextProviders/InsightsContentProvider';
import { InsightsStudioEditRecord } from 'contextProviders/InsightsChatProvider/conversationTypes';
import Button from 'kingpin/atoms/Button';
import Icon from 'kingpin/atoms/Icon';
import Typography from 'kingpin/atoms/Typography';

const List = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  margin-top: 8px;
`;

const Card = styled.div`
  padding: 8px 10px;
  border-radius: 8px;
  border: 1px solid var(--color-border-secondary);
  background-color: var(--color-bg-secondary);
`;

const Head = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
`;

const Summary = styled.div`
  flex: 1;
  min-width: 0;
`;

const Warning = styled.div`
  margin-top: 4px;
  color: var(--color-status-warning);
`;

// How an edit reads once done, undone, or when it couldn't be made
const doneLabel = (edit: InsightsStudioEditRecord): string => {
  if (edit.elementAction === 'add') {
    return 'Added: ';
  }
  if (edit.elementAction === 'remove') {
    return 'Removed: ';
  }
  if (edit.elementAction === 'move' || edit.elementAction === 'moveRow') {
    return 'Moved: ';
  }
  switch (edit.arrangeAction) {
    case 'move':
      return 'Moved: ';
    case 'resize':
      return 'Resized: ';
    case 'remove':
      return 'Removed: ';
    default:
      return edit.isNew ? (edit.isApplied ? 'Added: ' : 'Not added: ') : '';
  }
};

const NOT_ADDED_TEXT: Record<
  NonNullable<InsightsStudioEditRecord['reason']>,
  string
> = {
  noMetrics: 'None of its metrics are on this account',
  noRoom: "There's no room for it on the canvas",
  exists: "It's already on the canvas",
};

const undoneLabel = (edit: InsightsStudioEditRecord): string =>
  edit.arrangeAction === 'remove' || edit.elementAction === 'remove'
    ? 'Put back'
    : edit.isNew || edit.elementAction === 'add'
      ? 'Removed'
      : 'Undone';

const notAppliedText = (edit: InsightsStudioEditRecord): string => {
  if (edit.isNew) {
    return NOT_ADDED_TEXT[edit.reason ?? 'noMetrics'];
  }
  if (edit.arrangeAction) {
    return `Couldn't ${edit.arrangeAction} it there: it's no longer on the canvas, or there's no room`;
  }
  return 'Not applied: the block is no longer on the canvas';
};

const StudioEdit = ({
  edit,
  isUndone,
  canUndo,
  onUndo,
}: {
  edit: InsightsStudioEditRecord;
  isUndone: boolean;
  canUndo: boolean;
  onUndo: () => void;
}) => (
  <Card data-testid="insights-studio-edit">
    <Head>
      <Icon
        icon={edit.isApplied ? 'checkmark' : 'warning'}
        width={14}
        height={14}
        color={
          edit.isApplied
            ? 'var(--color-status-success)'
            : 'var(--color-status-warning)'
        }
      />
      <Summary>
        <Typography.Body type="Body 12">
          {isUndone
            ? `${undoneLabel(edit)}: ${edit.summary}`
            : `${doneLabel(edit)}${edit.summary}`}
        </Typography.Body>
      </Summary>
      {canUndo && (
        <Button
          size="Small"
          type="Ghost"
          label="Undo"
          onClick={onUndo}
          testId="insights-studio-edit-undo"
        />
      )}
    </Head>
    {!edit.isApplied && (
      <Warning>
        <Typography.Body type="Body 12" color="var(--color-status-warning)">
          {notAppliedText(edit)}
        </Typography.Body>
      </Warning>
    )}
    {edit.rejected.length > 0 && (
      <Warning>
        <Typography.Body type="Body 12" color="var(--color-status-warning)">
          Left out: {edit.rejected.join(', ')}
        </Typography.Body>
      </Warning>
    )}
    {edit.isApplied &&
      !isUndone &&
      [...(edit.missing ? [edit.missing] : []), ...(edit.warnings ?? [])].map(
        (warning) => (
          <Warning key={warning}>
            <Typography.Body type="Body 12" color="var(--color-status-warning)">
              {warning}
            </Typography.Body>
          </Warning>
        ),
      )}
  </Card>
);

const UndoAllRow = styled.div`
  display: flex;
  justify-content: flex-end;
`;

// The agent's edits to the Studio canvas in one answer, each with an Undo,
// and an Undo all when there are several to take back
const InsightsStudioEdits = ({
  edits,
}: {
  edits: InsightsStudioEditRecord[] | undefined;
}) => {
  const { getStudioBuilder } = useContext(InsightsContentContext);
  const [undoneIds, setUndoneIds] = useState<Set<string>>(new Set());
  // Read at render: undo only works on the Studio that applied the edit, so
  // a restored conversation (or a Studio since left) has none to offer
  const builder = getStudioBuilder();
  const canUndo = (edit: InsightsStudioEditRecord) =>
    edit.isApplied &&
    !undoneIds.has(edit.editId) &&
    !!builder?.canUndo(edit.editId);
  const undoable = (edits ?? []).filter(canUndo);

  const undo = useCallback(
    (toUndo: InsightsStudioEditRecord[]) => {
      // Newest first, so each block goes back to the state its snapshot was
      // taken from
      [...toUndo]
        .reverse()
        .forEach((edit) => getStudioBuilder()?.undo(edit.editId));
      setUndoneIds((prev) => {
        const next = new Set(prev);
        toUndo.forEach((edit) => next.add(edit.editId));
        return next;
      });
    },
    [getStudioBuilder],
  );

  if (!edits?.length) {
    return null;
  }
  return (
    <List>
      {undoable.length > 1 && (
        <UndoAllRow>
          <Button
            size="Small"
            type="Ghost"
            label={`Undo all (${undoable.length})`}
            onClick={() => undo(undoable)}
            testId="insights-studio-edit-undo-all"
          />
        </UndoAllRow>
      )}
      {edits.map((edit) => (
        <StudioEdit
          key={edit.editId}
          edit={edit}
          isUndone={undoneIds.has(edit.editId)}
          canUndo={canUndo(edit)}
          onUndo={() => undo([edit])}
        />
      ))}
    </List>
  );
};

export default InsightsStudioEdits;
